import { action, computed, makeObservable, observable } from 'mobx';
import { PaginationInput } from 'types/general';

interface ConstructorParams<SortBy, SortOrder, Filters extends Record<string, unknown>> {
  defaultSortBy: SortBy;
  defaultSortOrder: SortOrder;
  filters: Filters;
}

export type FiltersObject<K extends string = string> = {
  [Key in K]: unknown;
};

export class PaginationDelegate<SortBy, SortOrder, Filters extends FiltersObject> {
  private _page = 1;
  private _totalPages = 1;
  private _totalElements = 0;
  private _sortBy: SortBy;
  private _sortOrder: SortOrder;
  private _filters: Filters;
  private _defaultFilters: Filters;

  private _resetFilters(): void {
    this._filters = { ...this._defaultFilters };
  }

  public get page(): number {
    return this._page;
  }

  public get totalPages(): number {
    return this._totalPages;
  }

  public get totalElements(): number {
    return this._totalElements;
  }

  public get sortBy(): SortBy {
    return this._sortBy;
  }

  public get sortOrder(): SortOrder {
    return this._sortOrder;
  }

  public get filters(): Filters {
    return this._filters;
  }

  public get paginationInput(): PaginationInput<SortBy, SortOrder, Filters> {
    return { page: this.page, sortBy: this.sortBy, sortOrder: this._sortOrder, filter: this._filters };
  }

  public setTotalElements(totalElements: number): void {
    this._totalElements = totalElements;
  }

  public setPage(page: number): void {
    this._page = page;
  }

  public setSortBy(sortBy: SortBy): void {
    this._sortBy = sortBy;
  }

  public setSortOrder(sortOrder: SortOrder): void {
    this._sortOrder = sortOrder;
  }

  public setTotalPages(totalPages: number): void {
    this._totalPages = totalPages;
  }

  public setFilterValue<K extends keyof Filters>(key: K, value: Filters[K]): void {
    if (!(key in this._filters)) {
      throw new Error(
        `Attempted to set non-existing pagination filter (${String(key)})! Existing filters: ${Object.keys(
          this._filters,
        ).join(', ')}`,
      );
    }
    this._filters[key] = value;
  }

  public resetPagination(): void {
    this._resetFilters();
    this.setPage(1);
    this.setTotalPages(1);
    this.setTotalElements(0);
  }

  constructor({ defaultSortBy, defaultSortOrder, filters }: ConstructorParams<SortBy, SortOrder, Filters>) {
    this._sortBy = defaultSortBy;
    this._sortOrder = defaultSortOrder;
    this._filters = filters;
    this._defaultFilters = filters;

    makeObservable<
      PaginationDelegate<SortBy, SortOrder, Filters>,
      | '_page'
      | '_totalPages'
      | '_totalElements'
      | '_sortBy'
      | '_sortOrder'
      | '_filters'
      | '_defaultFilters'
      | '_resetFilters'
    >(this, {
      _page: observable,
      _totalElements: observable,
      _totalPages: observable,
      _sortBy: observable,
      _sortOrder: observable,
      _filters: observable.shallow,
      _defaultFilters: observable.shallow,
      page: computed,
      totalElements: computed,
      totalPages: computed,
      sortBy: computed,
      sortOrder: computed,
      filters: computed,
      paginationInput: computed,
      _resetFilters: action.bound,
      setTotalElements: action.bound,
      setPage: action.bound,
      setSortOrder: action.bound,
      setSortBy: action.bound,
      setTotalPages: action.bound,
      setFilterValue: action.bound,
    });
  }
}
